<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import AgentForm from './AgentForm.vue';
import AssistantAgentsAPI from 'dashboard/api/assistant/agents';

const props = defineProps({
  selectedAgent: {
    type: Object,
    default: () => ({}),
  },
  type: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  kind: {
    type: String,
    default: 'customer_agent',
    validator: value =>
      ['customer_agent', 'operator_assistant'].includes(value),
  },
});
const emit = defineEmits(['close', 'created', 'delete']);
const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const agentForm = ref(null);

// Onboarding wizard (У6): in create mode the operator first picks a niche
// template (or a blank agent); the chosen template seeds the form. Operator
// assistants have no niche templates — they skip the picker entirely.
const skipTemplates = props.kind === 'operator_assistant';
const templates = ref([]);
const selectedTemplate = ref(null);
const templatePicked = ref(props.type === 'edit' || skipTemplates);

const formAgent = computed(() =>
  props.type === 'edit' ? props.selectedAgent : selectedTemplate.value || {}
);

const fetchTemplates = async () => {
  try {
    const { data } = await AssistantAgentsAPI.templates();
    templates.value = data.payload || [];
  } catch (error) {
    templates.value = [];
  }
};

const pickTemplate = template => {
  selectedTemplate.value = template
    ? { name: template.name, config: { ...template.config } }
    : {};
  templatePicked.value = true;
};

const updateAgent = agentDetails =>
  store.dispatch('assistantAgents/update', {
    id: props.selectedAgent.id,
    ...agentDetails,
  });

// У ассистентов оператора свои тексты диалога — «ассистент», не «агент» (п.2).
const i18nSection = computed(() =>
  props.kind === 'operator_assistant'
    ? 'ASSISTANT.OPERATOR_ASSISTANTS'
    : 'ASSISTANT.AGENTS'
);
const i18nKey = computed(
  () => `${i18nSection.value}.${props.type.toUpperCase()}`
);

const createAgent = async agentDetails => {
  const newAgent = await store.dispatch('assistantAgents/create', agentDetails);
  emit('created', newAgent);
};

const handleSubmit = async updatedAgent => {
  try {
    if (props.type === 'edit') {
      await updateAgent(updatedAgent);
    } else {
      await createAgent(updatedAgent);
    }
    useAlert(t(`${i18nKey.value}.SUCCESS_MESSAGE`));
    dialogRef.value.close();
  } catch (error) {
    const errorMessage = error?.message || t(`${i18nKey.value}.ERROR_MESSAGE`);
    useAlert(errorMessage);
  }
};

const handleClose = () => {
  emit('close');
};

const handleCancel = () => {
  dialogRef.value.close();
};

onMounted(() => {
  if (props.type === 'create' && !skipTemplates) fetchTemplates();
});

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t(`${i18nKey}.TITLE`)"
    :description="t(`${i18nSection}.FORM_DESCRIPTION`)"
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
    @close="handleClose"
  >
    <div
      v-if="type === 'create' && !templatePicked"
      class="flex flex-col gap-3"
    >
      <p class="text-sm text-n-slate-11">
        {{ t('ASSISTANT.AGENTS.TEMPLATES.SUBTITLE') }}
      </p>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button
          v-for="template in templates"
          :key="template.key"
          type="button"
          class="flex items-start gap-3 p-3 text-start rounded-xl border border-n-weak bg-n-solid-1 hover:border-n-blue-8"
          @click="pickTemplate(template)"
        >
          <span class="text-2xl leading-none">{{ template.icon }}</span>
          <span class="flex flex-col gap-0.5">
            <span class="text-sm font-medium text-n-slate-12">
              {{ template.name }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ template.description }}
            </span>
          </span>
        </button>
      </div>
      <button
        type="button"
        class="mt-1 text-sm text-n-blue-11 hover:underline text-start"
        @click="pickTemplate(null)"
      >
        {{ t('ASSISTANT.AGENTS.TEMPLATES.BLANK') }}
      </button>
    </div>

    <template v-else>
      <button
        v-if="type === 'create' && !skipTemplates"
        type="button"
        class="flex items-center gap-1 mb-3 text-sm text-n-slate-11 hover:text-n-slate-12"
        @click="templatePicked = false"
      >
        <span class="i-lucide-arrow-left size-4" />
        {{ t('ASSISTANT.AGENTS.TEMPLATES.BACK') }}
      </button>
      <AgentForm
        ref="agentForm"
        :mode="type"
        :agent="formAgent"
        :kind="kind"
        @submit="handleSubmit"
        @cancel="handleCancel"
        @delete="emit('delete')"
      />
    </template>
    <template #footer />
  </Dialog>
</template>
